/**
 * Maps resolved PageFooter Data JSON to PageFooterNestedProps.
 *
 * Structured arrays pass through unflattened, with Data names normalized to the
 * PageFooterNestedProps names (`bottomBarRow1Links` -> `row1Links`, `bottomBarRow2Links` ->
 * `row2Links`). Elements missing a required field are dropped and absent arrays become [].
 * Only known props are emitted, at the root and inside every nested row: PageFooterNested
 * spreads unknown props onto its <footer>, so an unknown Data key must never reach it. An
 * authored URL that fails isNavigableUrl is treated as absent, so the element that needs it is
 * dropped and an optional one is omitted.
 *
 * Pure and synchronous. Malformed Data never throws.
 */

import {
  coerceBooleanKeys,
  filterValidElements,
  isValidDataObject,
  omitNonFiniteNumbers,
  omitNonStrings,
  omitUnsafeCssUrls,
  omitUnsafeUrls,
  pickKnownKeys,
} from '../../utils/resolverHelpers';
import type {
  ActionItem,
  BreadcrumbItem,
  FooterCountry,
  FooterLanguage,
  FooterLink,
  LinkColumn,
  PageFooterNestedProps,
  SocialLink,
} from './types';

/** Scalar PageFooterNestedProps copied from Data under their own names (types.ts:82-136). */
const SCALAR_KEYS: ReadonlyArray<keyof PageFooterNestedProps> = [
  'theme',
  'topBarText',
  'topBarLinkText',
  'topBarLinkUrl',
  'topBarCtaId',
  'logoImageUrl',
  'logoAlt',
  'logoUrl',
  'logoCtaId',
  'logoLdAppendix',
  'copyrightText',
  'disclaimerText',
  'footnotesText',
  'row1AriaLabel',
  'row2AriaLabel',
  'siteMapAriaLabel',
  'socialAriaLabel',
  'breadcrumbAriaLabel',
  'currentLanguage',
  'languagePickerHeading',
  'languagePickerAddPathLocale',
  'currentCountry',
  'countryPickerHeading',
  'countryPickerFootnote',
  'countrySpriteUrl',
];

/** Root scalars whose PageFooterNestedProps type is a string (or string union): every scalar but the boolean. */
const ROOT_STRING_KEYS: ReadonlyArray<string> = SCALAR_KEYS.filter((key) => key !== 'languagePickerAddPathLocale');
/** Root scalars whose PageFooterNestedProps type is boolean. */
const ROOT_BOOLEAN_KEYS: ReadonlyArray<string> = ['languagePickerAddPathLocale'];

/** Root scalars that hold an authored href. */
const ROOT_URL_KEYS: ReadonlyArray<string> = ['topBarLinkUrl', 'logoUrl'];
/** Root scalars PageFooterNested writes into an inline `backgroundImage: url(...)`. */
const ROOT_CSS_URL_KEYS: ReadonlyArray<string> = ['countrySpriteUrl'];
/** Element key that holds an authored href. */
const ELEMENT_URL_KEYS: ReadonlyArray<string> = ['url'];
/** Action keys PageFooterNested writes into an inline `backgroundImage: url(...)` past slot 1 when the action has a spriteY. */
const ACTION_CSS_URL_KEYS: ReadonlyArray<string> = ['iconUrl'];

/** ActionItem fields copied from Data; slot is assigned from position and onClick is never copied. */
const ACTION_KEYS: ReadonlyArray<keyof ActionItem> = ['iconUrl', 'iconAlt', 'label', 'spriteY'];

const FOOTER_LINK_REQUIRED_KEYS: (keyof FooterLink)[] = ['text', 'url'];
const SOCIAL_LINK_REQUIRED_KEYS: (keyof SocialLink)[] = ['url', 'iconUrl'];
const LINK_COLUMN_REQUIRED_KEYS: (keyof LinkColumn)[] = ['title', 'links'];
const ACTION_REQUIRED_KEYS: (keyof ActionItem)[] = ['iconUrl', 'label', 'slot'];
const BREADCRUMB_REQUIRED_KEYS: (keyof BreadcrumbItem)[] = ['text'];
const LANGUAGE_REQUIRED_KEYS: (keyof FooterLanguage)[] = ['code', 'name'];
const COUNTRY_REQUIRED_KEYS: (keyof FooterCountry)[] = ['code', 'name', 'url'];

/** Fields copied from each nested row (types.ts); every other key on a row is dropped. */
const SOCIAL_LINK_KEYS: ReadonlyArray<keyof SocialLink> = ['url', 'iconUrl', 'iconAlt', 'ctaId'];
const FOOTER_LINK_KEYS: ReadonlyArray<keyof FooterLink> = ['text', 'url', 'ctaId'];
const BREADCRUMB_KEYS: ReadonlyArray<keyof BreadcrumbItem> = ['text', 'url'];
const LANGUAGE_KEYS: ReadonlyArray<keyof FooterLanguage> = ['code', 'name'];
const COUNTRY_KEYS: ReadonlyArray<keyof FooterCountry> = ['code', 'name', 'url', 'spriteY'];

/** Row fields whose type is a string (types.ts); a non-string value there is treated as absent. */
const SOCIAL_LINK_STRING_KEYS: ReadonlyArray<string> = SOCIAL_LINK_KEYS;
const FOOTER_LINK_STRING_KEYS: ReadonlyArray<string> = FOOTER_LINK_KEYS;
const BREADCRUMB_STRING_KEYS: ReadonlyArray<string> = BREADCRUMB_KEYS;
const LANGUAGE_STRING_KEYS: ReadonlyArray<string> = LANGUAGE_KEYS;
const COUNTRY_STRING_KEYS: ReadonlyArray<string> = ['code', 'name', 'url'];
const LINK_COLUMN_STRING_KEYS: ReadonlyArray<string> = ['title'];
const ACTION_STRING_KEYS: ReadonlyArray<string> = ['iconUrl', 'iconAlt', 'label'];
/** Row fields whose type is a number (types.ts); a non-finite or non-number value there is treated as absent. */
const SPRITE_NUMBER_KEYS: ReadonlyArray<string> = ['spriteY'];

/** Slots are 1-based: slot 1 opens the language picker, slot 2 the country picker (types.ts:46-48). */
const FIRST_ACTION_SLOT = 1;

/** Reads a list that Data may store as an array or as a JSON string (PageFooter.tsx:277-287). */
function parseListValue(value: unknown, fieldName: string): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  if (value === '') {
    return [];
  }
  try {
    return JSON.parse(value);
  } catch {
    console.warn(`[PageFooter] Failed to parse ${fieldName} JSON. Rendering no ${fieldName}.`);
    return [];
  }
}

/**
 * Removes a non-string value of a string key, a non-finite value of a number key and a failing
 * `url` from every object element, so the caller's required-key filter decides.
 */
function withSafeValues(
  value: unknown,
  stringKeys: ReadonlyArray<string>,
  numberKeys: ReadonlyArray<string> = [],
): unknown {
  if (!Array.isArray(value)) {
    return value;
  }
  return value.map((element) => (isValidDataObject(element)
    ? omitUnsafeUrls(omitNonFiniteNumbers(omitNonStrings(element, stringKeys), numberKeys), ELEMENT_URL_KEYS)
    : element));
}

/** Drops non-string text and failing urls, keeps rows that have every required key, and copies only the listed keys. */
function toRows<T>(
  value: unknown,
  requiredKeys: (keyof T)[],
  keys: ReadonlyArray<keyof T>,
  stringKeys: ReadonlyArray<string>,
  numberKeys: ReadonlyArray<string> = [],
): T[] {
  return filterValidElements<T>(withSafeValues(value, stringKeys, numberKeys), requiredKeys)
    .map((row) => pickKnownKeys<T>(row as Record<string, unknown>, keys) as T);
}

function toFooterLinks(value: unknown): FooterLink[] {
  return toRows<FooterLink>(value, FOOTER_LINK_REQUIRED_KEYS, FOOTER_LINK_KEYS, FOOTER_LINK_STRING_KEYS);
}

function toLinkColumns(value: unknown): LinkColumn[] {
  return filterValidElements<LinkColumn>(withSafeValues(value, LINK_COLUMN_STRING_KEYS), LINK_COLUMN_REQUIRED_KEYS).map((column) => ({
    title: column.title,
    links: toFooterLinks(column.links),
  }));
}

/**
 * PageFooterNested writes an action's iconUrl into an inline `url(...)` only when the action is
 * past slot 1 (slot 1 renders the fixed language icon) and has a spriteY; otherwise the icon is
 * unused or an Image src, so the url() check applies to that branch only.
 */
function withSafeSpriteUrl(action: Record<string, unknown>, slot: number): Record<string, unknown> {
  return slot !== FIRST_ACTION_SLOT && typeof action.spriteY === 'number'
    ? omitUnsafeCssUrls(action, ACTION_CSS_URL_KEYS)
    : action;
}

function toActions(value: unknown): ActionItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  // The slot is the authored position, so a dropped first action leaves the second on slot 2.
  const withSlots = value.map((action, index) => {
    if (!isValidDataObject(action)) {
      return action;
    }
    const slot = FIRST_ACTION_SLOT + index;
    return {
      ...pickKnownKeys<ActionItem>(
        withSafeSpriteUrl(omitNonFiniteNumbers(omitNonStrings(action, ACTION_STRING_KEYS), SPRITE_NUMBER_KEYS), slot),
        ACTION_KEYS,
      ),
      slot,
    };
  });
  return filterValidElements<ActionItem>(withSlots, ACTION_REQUIRED_KEYS);
}

/**
 * Maps resolved PageFooter Data JSON to the props PageFooterNested renders.
 *
 * @param data - Resolved PageFooter Data JSON: `socialLinks`, `bottomBarRow1Links`,
 *   `bottomBarRow2Links`, `columns`, `actions`, `breadcrumbs`, `languages`, `countries`
 *   (the last three as arrays or JSON strings) and scalars under their PageFooterNestedProps
 *   names. Any other value, including null or an array, yields a footer with empty arrays.
 * @returns PageFooterNestedProps holding only known keys, with every array defined.
 */
export function resolvePageFooterProps(data: unknown): PageFooterNestedProps {
  const source: Record<string, unknown> = isValidDataObject(data) ? data : {};
  return {
    ...pickKnownKeys<PageFooterNestedProps>(
      coerceBooleanKeys(
        omitUnsafeCssUrls(omitUnsafeUrls(omitNonStrings(source, ROOT_STRING_KEYS), ROOT_URL_KEYS), ROOT_CSS_URL_KEYS),
        ROOT_BOOLEAN_KEYS,
      ),
      SCALAR_KEYS,
    ),
    socialLinks: toRows<SocialLink>(
      source.socialLinks, SOCIAL_LINK_REQUIRED_KEYS, SOCIAL_LINK_KEYS, SOCIAL_LINK_STRING_KEYS),
    row1Links: toFooterLinks(source.bottomBarRow1Links),
    row2Links: toFooterLinks(source.bottomBarRow2Links),
    columns: toLinkColumns(source.columns),
    actions: toActions(source.actions),
    breadcrumbs: toRows<BreadcrumbItem>(
      parseListValue(source.breadcrumbs, 'breadcrumbs'), BREADCRUMB_REQUIRED_KEYS, BREADCRUMB_KEYS, BREADCRUMB_STRING_KEYS),
    languages: toRows<FooterLanguage>(
      parseListValue(source.languages, 'languages'), LANGUAGE_REQUIRED_KEYS, LANGUAGE_KEYS, LANGUAGE_STRING_KEYS),
    countries: toRows<FooterCountry>(
      parseListValue(source.countries, 'countries'), COUNTRY_REQUIRED_KEYS, COUNTRY_KEYS, COUNTRY_STRING_KEYS,
      SPRITE_NUMBER_KEYS),
  };
}
